import React, { useState, useEffect, createContext, useMemo, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import {
  createTheme,
  ThemeProvider,
  type PaletteMode,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, CircularProgress, Typography } from "@mui/material";
import Layout from "./components/Layout/Layout";
import Login from "./pages/Login";
import Matches from "./pages/Cricket/Matches";
import MatchPlayers from "./pages/Cricket/MatchPlayers";
import MatchContentEditor from "./pages/Cricket/MatchContentEditor";
import SquadPlayers from "./pages/Cricket/SquadPlayers";
import Players from "./pages/Cricket/Players";
import Series from "./pages/Cricket/Series";
import Teams from "./pages/Cricket/Teams";
import Grounds from "./pages/Cricket/Grounds";
import MenuManagement from "./pages/App/MenuManagement";
import InstallAttributionDashboard from "./pages/App/InstallAttributionDashboard";
import MediaManagement from "./pages/MediaManagement";
import ExpoUpdates from "./pages/ExpoUpdates/ExpoUpdates";
import ExpoChannelDetail from "./pages/ExpoUpdates/ExpoChannelDetail";
import ExpoReleaseDetail from "./pages/ExpoUpdates/ExpoReleaseDetail";
import DashboardPage from "./pages/Dashboard/DashboardPage";

import { MediaDialogProvider } from "./components/media/MediaDialogProvider";

import {
  NotificationsMain,
  TemplateForm,
  ChannelForm,
  GroupForm,
  CampaignForm,
  TopicForm,
  SendTopicNotification,
} from "./pages/Connect";
import NotificationDetailsPage from "./pages/Connect/NotificationDetailsPage";
import UserList from "./pages/UserManagement/UserList";
import UserDetails from "./pages/UserManagement/UserDetails";
import ProtectedRoute from "./components/ProtectedRoute";
import { isAuthenticated } from "./services/auth";
import type {} from "@mui/x-data-grid/themeAugmentation";
import MatchPlayerGroupConfigResource from "./components/Fantasy/MatchPlayerGroupConfig";
import ArticleList from "./pages/CMS/ArticleList";
import ArticleEditor from "./pages/CMS/ArticleEditor";
import FaqList from "./pages/CMS/FaqList";
import FaqEditor from "./pages/CMS/FaqEditor";
import PageList from "./pages/CMS/PageList";
import PageEditor from "./pages/CMS/PageEditor";
import UspCardList from "./pages/CMS/UspCardList";
import TemplateList from './pages/CMS/TemplateList';
import TemplateEditor from './pages/CMS/TemplateEditor';
import PageHierarchy from './pages/CMS/PageHierarchy';
import MatchView from "./pages/Cricket/MatchView";
import SeriesView from "./pages/Cricket/SeriesView";
import PlayerView from "./pages/Cricket/PlayerView";
import TeamView from "./pages/Cricket/TeamView";
import AILineupPage from "./pages/Cricket/AILineupPage";
import DreamTeamAnalysis from "./pages/Cricket/DreamTeamAnalysis";
import {
  FootballLeagues,
  FootballMatches,
  FootballPlayers,
  FootballTeams,
  FootballStadiums,
  FootballReferees,
  FootballEvents,
  FootballMatchPlayers,
  FootballSquadPlayers,
} from './pages/football';
import LeagueForm from './pages/football/LeagueForm';
import PlayerForm from './pages/football/PlayerForm';
import TeamForm from './pages/football/TeamForm';
import StadiumForm from './pages/football/StadiumForm';
import RefereeForm from './pages/football/RefereeForm';
import MatchForm from './pages/football/MatchForm';
import EventForm from './pages/football/EventForm';
import MatchPlayerForm from './pages/football/MatchPlayerForm';
import SquadPlayerForm from './pages/football/SquadPlayerForm';
import Tournaments from "./pages/Xtract/Tournaments";
import SportEvents from "./pages/Xtract/SportEvents";
import ManualActions from "./pages/Xtract/ManualActions";
import NewSeries from './pages/Cricket/NewSeries';
import DelphiContentEditor from './pages/Delphi/DelphiContentEditor';
import DelphiSessionsList from './pages/Delphi/DelphiSessionsList';
import PromptTemplates from './pages/Delphi/PromptTemplates';
import ContentDashboard from './pages/ContentDashboard';
import MarketplaceDashboard from './pages/Marketplace';
import ChurnedUsers from './pages/Marketplace/ChurnedUsers';

const SupportLayout = React.lazy(() => import('./components/Support/SupportLayout'));
const ConversationDetail = React.lazy(() => import('./pages/Support/ConversationDetail'));

// Lazy load Team Workflow
const TeamWorkflowDashboard = React.lazy(() => import('./pages/TeamWorkflow/TeamWorkflowDashboard'));

// Define the Black Russian color palette
const blackRussian = {
  50: "#e6e6e8",
  100: "#c1c1c5",
  200: "#9798a0",
  300: "#6d6f7a",
  400: "#4e505e",
  500: "#2f3142", // Black Russian main color
  600: "#2a2c3c",
  700: "#232533",
  800: "#1d1f2b",
  900: "#14151d",
};

// Create a theme context to manage theme state
export const ThemeContext = createContext({
  toggleColorMode: () => {},
  mode: "light" as PaletteMode,
});

const App: React.FC = () => {
  // Detect the preferred color scheme from system settings
  const prefersDarkMode =
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  // Check if user has a stored preference in localStorage
  const storedMode = localStorage.getItem("themeMode") as PaletteMode | null;

  // Initialize theme mode state with stored preference or system preference
  const [mode, setMode] = useState<PaletteMode>(
    storedMode || (prefersDarkMode ? "dark" : "light"),
  );

  // Create the theme context value
  const themeContextValue = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          // Store the user's preference in localStorage
          localStorage.setItem("themeMode", newMode);
          return newMode;
        });
      },
      mode,
    }),
    [mode],
  );

  // Create the theme with our custom colors
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: blackRussian[500],
            light: blackRussian[300],
            dark: blackRussian[700],
            contrastText: "#ffffff",
            ...blackRussian,
          },
          ...(mode === "dark"
            ? {
                background: {
                  default: blackRussian[900],
                  paper: blackRussian[800],
                },
              }
            : {
                background: {
                  default: "#f8f9fa",
                  paper: "#ffffff",
                },
              }),
        },
        typography: {
          fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          button: {
            textTransform: "none",
            fontWeight: 600,
          },
        },
        shape: {
          borderRadius: 8,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                borderRadius: 8,
                ...(mode === "dark" && {
                  // Add better contrast for contained buttons in dark mode
                  "&.MuiButton-contained": {
                    backgroundColor: "#90caf9", // Light blue for better visibility
                    color: blackRussian[900],
                    "&:hover": {
                      backgroundColor: "#64b5f6", // Slightly darker on hover
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(144, 202, 249, 0.3)",
                      color: "rgba(0, 0, 0, 0.5)",
                    },
                  },
                  // Improve outlined buttons visibility in dark mode
                  "&.MuiButton-outlined": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                    color: "rgba(255, 255, 255, 0.9)",
                    "&:hover": {
                      borderColor: "#90caf9",
                      backgroundColor: "rgba(144, 202, 249, 0.08)",
                    },
                    "&.Mui-disabled": {
                      borderColor: "rgba(255, 255, 255, 0.3)",
                      color: "rgba(255, 255, 255, 0.4)",
                    },
                  },
                  // Text buttons in dark mode
                  "&.MuiButton-text": {
                    color: "#90caf9",
                    "&:hover": {
                      backgroundColor: "rgba(144, 202, 249, 0.08)",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(255, 255, 255, 0.3)",
                    },
                  },
                }),
              },
              // Style for different button variants that are consistent in both light and dark modes
              containedPrimary: {
                ...(mode === "dark" && {
                  backgroundColor: "#90caf9",
                  color: blackRussian[900],
                  "&:hover": {
                    backgroundColor: "#64b5f6",
                  },
                }),
              },
              outlinedPrimary: {
                ...(mode === "dark" && {
                  borderColor: "#90caf9",
                  color: "#90caf9",
                  "&:hover": {
                    borderColor: "#64b5f6",
                    backgroundColor: "rgba(144, 202, 249, 0.08)",
                  },
                }),
              },
              textPrimary: {
                ...(mode === "dark" && {
                  color: "#90caf9",
                  "&:hover": {
                    backgroundColor: "rgba(144, 202, 249, 0.08)",
                  },
                }),
              },
            },
          },
          MuiDataGrid: {
            styleOverrides: {
              root: {
                border: "none",
                ...(mode === "dark" && {
                  "& .MuiDataGrid-columnHeaders": {
                    borderColor: "rgba(255, 255, 255, 0.12)",
                  },
                  "& .MuiDataGrid-cell": {
                    borderColor: "rgba(255, 255, 255, 0.08)",
                  },
                  "& .MuiTablePagination-root": {
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: "rgba(255, 255, 255, 0.9)",
                  },
                  "& .MuiDataGrid-toolbarContainer button": {
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                }),
              },
              row: {
                // Add hover styles specific to dark/light mode
                ...(mode === "dark" && {
                  "&:hover": {
                    backgroundColor: blackRussian[700] + " !important",
                    // Ensure text remains readable on hover
                    color: "#ffffff",
                  },
                  // Make the row text more visible in dark mode
                  color: "rgba(255, 255, 255, 0.85)",
                }),
              },
              columnSeparator: {
                ...(mode === "dark" && {
                  color: "rgba(255, 255, 255, 0.1)",
                }),
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                ...(mode === "dark" && {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.23)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#90caf9", // Use a blue shade that works in dark mode
                  },
                }),
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                ...(mode === "dark" && {
                  color: "rgba(255, 255, 255, 0.7)",
                  "&.Mui-focused": {
                    color: "#90caf9", // Match the focused border color
                  },
                }),
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                ...(mode === "dark" && {
                  "& .MuiFormHelperText-root": {
                    color: "rgba(255, 255, 255, 0.6)",
                  },
                }),
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              icon: {
                ...(mode === "dark" && {
                  color: "rgba(255, 255, 255, 0.5)",
                }),
              },
              select: {
                ...(mode === "dark" && {
                  "&:focus": {
                    backgroundColor: "transparent",
                  },
                }),
              },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                ...(mode === "dark" && {
                  "&.Mui-selected": {
                    backgroundColor: "rgba(144, 202, 249, 0.16)",
                    "&:hover": {
                      backgroundColor: "rgba(144, 202, 249, 0.24)",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                  },
                }),
              },
            },
          },
          MuiPopover: {
            styleOverrides: {
              paper: {
                ...(mode === "dark" && {
                  backgroundColor: blackRussian[800],
                  boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.3)",
                }),
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                ...(mode === "dark" && {
                  color: "rgba(255, 255, 255, 0.7)",
                  "&.Mui-selected": {
                    color: "#90caf9", // Bright blue color for selected tab in dark mode
                  },
                }),
              },
            },
          },
          MuiTabs: {
            styleOverrides: {
              indicator: {
                ...(mode === "dark" && {
                  backgroundColor: "#90caf9", // Match the selected tab text color
                }),
              },
              root: {
                ...(mode === "dark" && {
                  borderColor: "rgba(255, 255, 255, 0.23)",
                }),
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                borderRadius: 10,
                ...(mode === "dark" && {
                  backgroundImage: "none", // Remove default background gradient in dark mode
                }),
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                ...(mode === "dark" && {
                  backgroundColor: blackRussian[700], // Slightly lighter than background for contrast
                }),
              },
            },
          },
          MuiDivider: {
            styleOverrides: {
              root: {
                ...(mode === "dark" && {
                  borderColor: "rgba(255, 255, 255, 0.12)", // More visible dividers in dark mode
                }),
              },
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              root: {
                ...(mode === "dark" && {
                  color: "rgba(255, 255, 255, 0.7)",
                  "&.Mui-checked": {
                    color: "#90caf9",
                  },
                }),
              },
            },
          },
          MuiRadio: {
            styleOverrides: {
              root: {
                ...(mode === "dark" && {
                  color: "rgba(255, 255, 255, 0.7)",
                  "&.Mui-checked": {
                    color: "#90caf9",
                  },
                }),
              },
            },
          },
          MuiListItem: {
            styleOverrides: {
              root: {
                ...(mode === "dark" && {
                  "&.Mui-selected": {
                    backgroundColor: "rgba(144, 202, 249, 0.16)",
                    "&:hover": {
                      backgroundColor: "rgba(144, 202, 249, 0.24)",
                    },
                  },
                }),
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                borderRadius: 6,
                fontWeight: 600,
                ...(mode === "dark" && {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  "&.MuiChip-filled": {
                    backgroundColor: "rgba(144, 202, 249, 0.16)",
                  },
                }),
              },
              outlined: {
                ...(mode === "dark" && {
                  borderColor: "rgba(255, 255, 255, 0.23)",
                  color: "rgba(255, 255, 255, 0.9)",
                }),
              },
              label: {
                ...(mode === "dark" && {
                  color: "rgba(255, 255, 255, 0.9)",
                }),
              },
              deleteIcon: {
                ...(mode === "dark" && {
                  color: "rgba(255, 255, 255, 0.7)",
                  "&:hover": {
                    color: "rgba(255, 255, 255, 1)",
                  },
                }),
              },
              icon: {
                ...(mode === "dark" && {
                  color: "rgba(255, 255, 255, 0.7)",
                }),
              },
              colorPrimary: {
                ...(mode === "dark" && {
                  "&.MuiChip-outlined": {
                    borderColor: "#90caf9",
                    color: "#90caf9",
                  },
                  "&.MuiChip-filled": {
                    backgroundColor: "rgba(144, 202, 249, 0.24)",
                    color: "#90caf9",
                  },
                }),
              },
              colorSecondary: {
                ...(mode === "dark" && {
                  "&.MuiChip-outlined": {
                    borderColor: "#ce93d8",
                    color: "#ce93d8",
                  },
                  "&.MuiChip-filled": {
                    backgroundColor: "rgba(206, 147, 216, 0.24)",
                    color: "#ce93d8",
                  },
                }),
              },
            },
          },
        },
      }),
    [mode],
  );

  // Listen for changes in system color scheme preference
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update mode based on system preference if user hasn't manually set a preference
      if (!localStorage.getItem("themeMode")) {
        setMode(e.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MediaDialogProvider>
          <Router>
            <Routes>
              <Route
                path="/login"
                element={
                  isAuthenticated() ? <Navigate to="/" replace /> : <Login />
                }
              />
              
              {/* Support Chat Routes - Standalone Layout (no sidebar) - Lazy Loaded */}
              <Route
                path="/support"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center', 
                          height: '100vh' 
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    }>
                      <SupportLayout />
                    </Suspense>
                  </ProtectedRoute>
                }
              >
                <Route index element={
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h6" color="text.secondary">Select a conversation to start chatting</Typography>
                  </Box>
                } />
                <Route path=":id" element={<ConversationDetail />} />
              </Route>

              {/* Team Workflow Dashboard - Standalone Layout */}
              <Route
                path="/team-workflow"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                        <CircularProgress />
                      </Box>
                    }>
                      <TeamWorkflowDashboard />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="content-dashboard" element={<ContentDashboard />} />
                <Route path="marketplace" element={<MarketplaceDashboard />} />
                <Route path="marketplace/churn" element={<ChurnedUsers />} />
                <Route path="matches" element={<Matches />} />
                <Route
                  path="matches/:id/content"
                  element={<MatchContentEditor />}
                />
                <Route path="matches/:id/view" element={<MatchView />} />
                <Route path="ai-lineup" element={<AILineupPage />} />
                <Route path="match-players" element={<MatchPlayers />} />
                <Route path="squad-players" element={<SquadPlayers />} />
                <Route path="players" element={<Players />} />
                <Route path="players/:id/view" element={<PlayerView />} />
                <Route path="series" element={<Series />} />
                <Route path="series/:id/view" element={<SeriesView />} />
                <Route path="tournaments" element={<Tournaments />} />
                <Route path="teams" element={<Teams />} />
                <Route path="teams/:id/view" element={<TeamView />} />
                <Route path="grounds" element={<Grounds />} />
                <Route path="dream-team-analysis" element={<DreamTeamAnalysis />} />
                <Route path="menu-management" element={<MenuManagement />} />
                <Route path="app-attribution" element={<InstallAttributionDashboard />} />
                <Route path="media" element={<MediaManagement />} />
                <Route path="expo-updates" element={<ExpoUpdates />} />
                <Route
                  path="expo-updates/channels/:id"
                  element={<ExpoChannelDetail />}
                />
                <Route
                  path="expo-updates/releases/:id"
                  element={<ExpoReleaseDetail />}
                />

                {/* Notification System Routes */}
                <Route path="connect" element={<NotificationsMain />} />
                <Route
                  path="connect/details/:id"
                  element={<NotificationDetailsPage />}
                />
                <Route path="connect/templates/new" element={<TemplateForm />} />
                <Route path="connect/templates/:id" element={<TemplateForm />} />
                <Route path="connect/channels/new" element={<ChannelForm />} />
                <Route path="connect/channels/:id" element={<ChannelForm />} />
                <Route path="connect/groups/new" element={<GroupForm />} />
                <Route path="connect/groups/:id" element={<GroupForm />} />
                <Route path="connect/topics/new" element={<TopicForm />} />
                <Route path="connect/topics/:id" element={<TopicForm />} />
                <Route path="connect/send" element={<SendTopicNotification />} />
                <Route path="connect/campaigns/new" element={<CampaignForm />} />
                <Route path="connect/campaigns/:id" element={<CampaignForm />} />

                {/* User Management Routes */}
                <Route path="users" element={<UserList />} />
                <Route path="users/:id" element={<UserDetails />} />
                <Route path="manual-actions" element={<ManualActions />} />

                <Route
                  path="match-player-group-configs/*"
                  element={<MatchPlayerGroupConfigResource />}
                />
                <Route path="sport-events" element={<SportEvents />} />

                {/* CMS Routes */}
                <Route path="cms">
                  <Route path="articles" element={<ArticleList />} />
                  <Route path="articles/:id/edit" element={<ArticleEditor />} />
                  <Route path="articles/:id/view" element={<ArticleEditor />} />
                  <Route path="articles/new" element={<ArticleEditor />} />
                  <Route path="faqs" element={<FaqList />} />
                  <Route path="faqs/:id" element={<FaqEditor />} />
                  <Route path="pages" element={<PageList />} />
                  <Route path="pages/hierarchy" element={<PageHierarchy fullPageMode={true} />} />
                  <Route path="pages/new" element={<PageEditor />} />
                  <Route path="pages/:id/edit" element={<PageEditor />} />
                  <Route path="pages/:id/view" element={<PageEditor />} />
                  <Route path="usp-cards" element={<UspCardList />} />
                  <Route path="templates" element={<TemplateList />} />
                  <Route path="templates/new" element={<TemplateEditor />} />
                  <Route path="templates/:id" element={<TemplateEditor />} />
                </Route>

                {/* Delphi AI Content Engine Routes */}
                <Route path="delphi" element={<DelphiSessionsList />} />
                <Route path="delphi/new" element={<DelphiContentEditor />} />
                <Route path="delphi/:sessionId" element={<DelphiContentEditor />} />
                <Route path="delphi/prompts" element={<PromptTemplates />} />

                {/* Football Admin Routes */}
                <Route path="football">
                  <Route path="leagues" element={<FootballLeagues />} />
                  <Route path="leagues/:id/edit" element={<LeagueForm />} />
                  <Route path="leagues/new" element={<LeagueForm />} />
                  <Route path="matches" element={<FootballMatches />} />
                  <Route path="matches/:id/edit" element={<MatchForm />} />
                  <Route path="matches/new" element={<MatchForm />} />
                  <Route path="players" element={<FootballPlayers />} />
                  <Route path="players/:id/edit" element={<PlayerForm />} />
                  <Route path="players/new" element={<PlayerForm />} />
                  <Route path="match-players" element={<FootballMatchPlayers />} />
                  <Route path="match-players/:id/edit" element={<MatchPlayerForm />} />
                  <Route path="match-players/new" element={<MatchPlayerForm />} />
                  <Route path="squad-players" element={<FootballSquadPlayers />} />
                  <Route path="squad-players/:id/edit" element={<SquadPlayerForm />} />
                  <Route path="squad-players/new" element={<SquadPlayerForm />} />
                  <Route path="teams" element={<FootballTeams />} />
                  <Route path="teams/:id/edit" element={<TeamForm />} />
                  <Route path="teams/new" element={<TeamForm />} />
                  <Route path="stadiums" element={<FootballStadiums />} />
                  <Route path="stadiums/:id/edit" element={<StadiumForm />} />
                  <Route path="stadiums/new" element={<StadiumForm />} />
                  <Route path="referees" element={<FootballReferees />} />
                  <Route path="referees/:id/edit" element={<RefereeForm />} />
                  <Route path="referees/new" element={<RefereeForm />} />
                  <Route path="events" element={<FootballEvents />} />
                  <Route path="events/:id/edit" element={<EventForm />} />
                  <Route path="events/new" element={<EventForm />} />
                </Route>
                <Route path="/cricket/series/new" element={<NewSeries />} />


              </Route>
            </Routes>
          </Router>
        </MediaDialogProvider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default App;
